/**
 * Full-flow hydrate reconcile for strategy questionnaire session.
 *
 * Belongs here: wipe-once / seed / ignore-after-done → ApplyHydration.
 * Does not belong here: answering, leave, PrepareStageEdit, or navigation.
 */
import { useLayoutEffect, useRef, type Dispatch } from 'react'
import type {
  StrategyQuestionnairePayload,
  StrategyQuestionnaireAnswersMap,
  QuestionnaireStatus
} from '../../shared/types/questionnaire.types'
import {
  getFullFlowHydrationPlan,
  isSameAppliedHydrationStatus,
  INITIAL_FULL_FLOW_HYDRATION_PROGRESS,
  type FullFlowHydrationProgress
} from '../../shared/sessionHydrationPolicy'
import { buildInitialStrategyAnswersFromPayload } from '../../question-utils/strategyAnswers'
import type { ExpertReviewStatus } from '../../providers/ExpertReviewStatusContext'
import { StrategySessionActionType, type StrategySessionAction } from './strategySessionReducer'

export type UseStrategySessionHydrationArgs = {
  stageIdFromPathParam: string | undefined
  rawQuestionnaireData: StrategyQuestionnairePayload | undefined
  committedAnswers: StrategyQuestionnaireAnswersMap
  expertReviewStatus: ExpertReviewStatus
  dispatch: Dispatch<StrategySessionAction>
}

/**
 * Applies FullFlow hydration from /status.
 * Edit routes are skipped — PrepareStageEdit owns those answers.
 */
export function useStrategySessionHydration({
  stageIdFromPathParam,
  rawQuestionnaireData,
  committedAnswers,
  expertReviewStatus,
  dispatch
}: UseStrategySessionHydrationArgs) {
  const { isStatusLoading, isStatusError, questionnaireStatus } = expertReviewStatus
  // Last status we already applied for wipe/seed — skip repeats while answering
  const lastAppliedStatusRef = useRef<QuestionnaireStatus | null | undefined>(undefined)
  const hydrationProgressRef = useRef<FullFlowHydrationProgress>(INITIAL_FULL_FLOW_HYDRATION_PROGRESS)
  const hasLocalCommittedAnswers = Object.keys(committedAnswers).length > 0

  useLayoutEffect(() => {
    if (stageIdFromPathParam?.trim()) return
    if (isStatusLoading) return
    if (isStatusError) return

    const plan = getFullFlowHydrationPlan(
      questionnaireStatus,
      hydrationProgressRef.current,
      hasLocalCommittedAnswers
    )
    hydrationProgressRef.current = plan.hydrationProgress

    if (plan.action === 'skip') return

    if (plan.action === 'wipe') {
      dispatch({
        type: StrategySessionActionType.ApplyHydration,
        draftAnswers: {},
        committedAnswers: {},
        resetCursorToStart: plan.resetCursorToStart,
        hasReachedReviewOnce: false,
        answersVersionPolicy: 'wipe'
      })
      lastAppliedStatusRef.current = questionnaireStatus
      return
    }

    // seed — same applied done-status already applied → avoid clobbering
    if (isSameAppliedHydrationStatus(lastAppliedStatusRef.current, questionnaireStatus)) return
    if (!rawQuestionnaireData) return

    const answers = buildInitialStrategyAnswersFromPayload({
      ...rawQuestionnaireData,
      stages: rawQuestionnaireData.stages ?? []
    })
    // Payload can lag POST success — don't replace good local answers with an empty seed
    if (Object.keys(answers)?.length === 0) return

    dispatch({
      type: StrategySessionActionType.ApplyHydration,
      draftAnswers: answers,
      committedAnswers: answers,
      resetCursorToStart: plan.resetCursorToStart,
      hasReachedReviewOnce: true,
      answersVersionPolicy: 'seed'
    })
    lastAppliedStatusRef.current = questionnaireStatus
  }, [
    rawQuestionnaireData,
    hasLocalCommittedAnswers,
    stageIdFromPathParam,
    isStatusLoading,
    isStatusError,
    questionnaireStatus,
    dispatch
  ])
}
